import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  message
} from 'antd';
import {
  AppstoreOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
  SearchOutlined,
  TeamOutlined,
  TrophyOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { useAuth } from '../../contexts/AuthContext';
import api, { classAPI } from '../../services/api';
import studentAPI from '../../services/studentAPI';
import PointsItemSelector from '../../components/PointsItemSelector';
import pointsTemplatesAPI from '../../services/pointsTemplatesAPI';
import SystemTemplates from '../../components/points-templates/SystemTemplates';
import CustomTemplates from '../../components/points-templates/CustomTemplates';
import TemplateItems from '../../components/points-templates/TemplateItems';
import { CATEGORY_LIST, getReasonsForCategory } from '../../constants/pointsCategories';
import { downloadCsv, fetchAllPages } from '../../utils/csvExport';

const { TabPane } = Tabs;
const { Option } = Select;

const normalizePointType = (value) => (value === 'add' ? 'add' : 'subtract');
const toReasonType = (value) => (normalizePointType(value) === 'add' ? 'add' : 'deduct');
const getClassLabel = (item) => item?.class_name || item?.name || '未命名班级';
const toNumber = (value) => Number(value || 0);
const toSignedPoints = (pointType, points) => {
  const value = Math.abs(Number(points || 0));
  return normalizePointType(pointType) === 'add' ? value : -value;
};

/** 左侧小圆框（单选视觉），与文字组成可点选项 */
const RadioDot = ({ checked }) => (
  <span
    style={{
      width: 14,
      height: 14,
      borderRadius: '50%',
      border: `2px solid ${checked ? '#1677ff' : '#bfbfbf'}`,
      background: '#fff',
      flexShrink: 0,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxSizing: 'border-box'
    }}
    aria-hidden
  >
    {checked ? (
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: '#1677ff'
        }}
      />
    ) : null}
  </span>
);

const ClickRadioOption = ({ children, checked, onPick }) => (
  <div
    role="radio"
    aria-checked={checked}
    tabIndex={0}
    onClick={onPick}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onPick();
      }
    }}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '6px 12px',
      borderRadius: 8,
      border: `1px solid ${checked ? '#91caff' : '#d9d9d9'}`,
      background: checked ? '#e6f4ff' : '#fafafa',
      cursor: 'pointer',
      userSelect: 'none',
      transition: 'background 0.15s, border-color 0.15s'
    }}
  >
    <RadioDot checked={checked} />
    <span style={{ fontSize: 14, color: checked ? '#0958d9' : '#262626', lineHeight: 1.3 }}>{children}</span>
  </div>
);

/** 分类：点击选择，带单选小圆框 */
const CategoryClickPicker = ({ value, onChange, onAfterPick }) => (
  <Space wrap size={[8, 8]} style={{ width: '100%' }}>
    {CATEGORY_LIST.map((cat) => (
      <ClickRadioOption
        key={cat}
        checked={value === cat}
        onPick={() => {
          if (value === cat) return;
          onChange(cat);
          onAfterPick?.();
        }}
      >
        {cat}
      </ClickRadioOption>
    ))}
  </Space>
);

/** 原因：推荐项点击选择（带小圆框）+ 可手动输入 */
const ReasonClickPicker = ({ value, onChange, reasonOptions = [] }) => (
  <div>
    {reasonOptions.length > 0 ? (
      <Space wrap size={[8, 8]} style={{ width: '100%', marginBottom: 10 }}>
        {reasonOptions.map((text) => (
          <ClickRadioOption key={text} checked={value === text} onPick={() => onChange(text)}>
            {text}
          </ClickRadioOption>
        ))}
      </Space>
    ) : (
      <div style={{ color: '#8c8c8c', fontSize: 13, marginBottom: 8 }}>请先选择分类；也可直接在下方填写原因。</div>
    )}
    <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="点击上方选项或填写其他原因" allowClear />
  </div>
);

const StudentPointsWorkbench = () => {
  const { user } = useAuth();
  const isTeacher = user?.role === 'teacher' || user?.userType === 'teacher';
  const [activeTab, setActiveTab] = useState(isTeacher ? 'register' : 'records');
  const [pageLoading, setPageLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [seatsLoading, setSeatsLoading] = useState(false);
  const [seats, setSeats] = useState([]);
  const [seatLayout, setSeatLayout] = useState({ rows: 0, cols: 0, aislePositions: [] });
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [selectorVisible, setSelectorVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [batchDraft, setBatchDraft] = useState({ pointType: 'add', category: null });
  const [submitting, setSubmitting] = useState(false);
  const [pointsData, setPointsData] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [recordFilters, setRecordFilters] = useState({
    studentId: null,
    pointType: null,
    category: null,
    startDate: null,
    endDate: null
  });
  const [recordsPagination, setRecordsPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [statistics, setStatistics] = useState({ totalAdded: 0, totalDeducted: 0, netPoints: 0 });
  const [rankingLoading, setRankingLoading] = useState(false);
  const [rankingData, setRankingData] = useState([]);
  const [rankingLimit, setRankingLimit] = useState(20);
  const [templates, setTemplates] = useState([]);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [templateItems, setTemplateItems] = useState([]);
  const [batchForm] = Form.useForm();
  const [recordForm] = Form.useForm();
  const [recordModalVisible, setRecordModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [recordDraft, setRecordDraft] = useState({ pointType: 'add', category: null });

  const selectedClass = classes.find((item) => String(item.id) === String(selectedClassId)) || null;
  const seatedStudents = seats.filter((item) => item.student_id && item.student_name);
  const classStudents = students.filter((item) => !selectedClassId || String(item.class_id) === String(selectedClassId));
  const showSeatGrid =
    seatedStudents.length > 0 && toNumber(seatLayout.rows) > 0 && toNumber(seatLayout.cols) > 0;
  const rosterForRegister = classStudents
    .filter((s) => s.id && s.student_name)
    .map((s) => ({ student_id: s.id, student_name: s.student_name }));
  const registerSelectableList = showSeatGrid ? seatedStudents : rosterForRegister;
  const selectedRegisterEntries = registerSelectableList.filter((item) => selectedStudentIds.includes(item.student_id));
  const quickTemplateItems = templateItems
    .filter((item) => normalizePointType(item.item_type) === batchDraft.pointType)
    .slice(0, 8);

  useEffect(() => {
    fetchBaseData();
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedClassId) {
      fetchSeats(selectedClassId);
    } else {
      setSeats([]);
      setSeatLayout({ rows: 0, cols: 0, aislePositions: [] });
      setSelectedStudentIds([]);
    }
  }, [selectedClassId]);

  useEffect(() => {
    fetchPointsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClassId, recordsPagination.current, recordsPagination.pageSize, recordFilters]);

  useEffect(() => {
    if (activeTab === 'ranking') {
      fetchRankingData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedClassId, rankingLimit]);

  const fetchBaseData = async () => {
    setPageLoading(true);
    try {
      const [classesRes, studentsRes] = await Promise.all([
        // Let the API apply the teacher's active class scope. A QR session can
        // contain an older school membership, which must not hide valid classes.
        classAPI.getAll({ pageSize: 200, schoolId: user?.workbenchSchoolId }),
        api.get('/students', { params: { schoolId: user?.workbenchSchoolId } })
      ]);
      const classList = classesRes.data?.data || [];
      setClasses(classList);
      setStudents(studentsRes.data?.data || []);
      if (classList.length > 0) {
        setSelectedClassId((current) => current || classList[0].id);
      }
    } catch (error) {
      console.error('获取积分工作台数据失败:', error);
      message.error('获取班级或学生数据失败');
    } finally {
      setPageLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await pointsTemplatesAPI.getMyTemplates();
      if (response.data.success) {
        const list = response.data.data || [];
        setTemplates(list);
        const first = list.find((item) => item.is_default) || list[0] || null;
        if (first) {
          setCurrentTemplate(first);
          loadTemplateItems(first.id);
        }
      }
    } catch (error) {
      console.error('获取积分模板失败:', error);
    }
  };

  const loadTemplateItems = async (templateId) => {
    try {
      const response = await pointsTemplatesAPI.getTemplateDetail(templateId);
      if (response.data.success) {
        setTemplateItems(response.data.data.items || []);
      }
    } catch (error) {
      console.error('加载模板项目失败:', error);
      message.error('加载模板项目失败');
    }
  };

  const fetchSeats = async (classId) => {
    setSeatsLoading(true);
    try {
      const response = await api.get(`/seats/${classId}`);
      if (response.data.success) {
        const seatList = response.data.data?.seats || [];
        const layout = response.data.data?.layout || {};
        const maxRow = seatList.length ? Math.max(...seatList.map((item) => toNumber(item.row_number))) : 0;
        const maxCol = seatList.length ? Math.max(...seatList.map((item) => toNumber(item.col_number))) : 0;
        setSeats(seatList);
        setSeatLayout({
          rows: toNumber(layout.rows) || maxRow,
          cols: toNumber(layout.cols) || maxCol,
          aislePositions: Array.isArray(layout.aislePositions) ? layout.aislePositions : []
        });
        setSelectedStudentIds([]);
      }
    } catch (error) {
      console.error('获取座位布局失败:', error);
      setSeats([]);
      setSeatLayout({ rows: 0, cols: 0, aislePositions: [] });
      message.error('获取座位布局失败');
    } finally {
      setSeatsLoading(false);
    }
  };

  const fetchPointsData = async () => {
    setRecordsLoading(true);
    try {
      const params = {
        page: recordsPagination.current,
        pageSize: recordsPagination.pageSize,
        schoolId: selectedClass?.school_id || selectedClass?.schoolId || user?.schoolId || undefined,
        classId: selectedClassId || undefined,
        ...recordFilters
      };
      const response = await studentAPI.points.getPoints(params);
      if (response.data.success) {
        const list = response.data.data || [];
        setPointsData(list);
        setRecordsPagination((prev) => ({ ...prev, total: response.data.pagination?.total || 0 }));
        const totalAdded = list.filter((item) => Number(item.points) > 0).reduce((sum, item) => sum + Number(item.points || 0), 0);
        const totalDeducted = list.filter((item) => Number(item.points) < 0).reduce((sum, item) => sum + Math.abs(Number(item.points || 0)), 0);
        setStatistics({ totalAdded, totalDeducted, netPoints: totalAdded - totalDeducted });
      }
    } catch (error) {
      console.error('获取积分记录失败:', error);
      message.error('获取积分记录失败');
    } finally {
      setRecordsLoading(false);
    }
  };

  const fetchRankingData = async () => {
    if (!selectedClassId) {
      setRankingData([]);
      return;
    }
    setRankingLoading(true);
    try {
      const response = await studentAPI.points.getClassRanking(selectedClassId, { limit: rankingLimit });
      if (response.data.success) {
        setRankingData(response.data.data?.ranking || []);
      } else {
        message.error(response.data.message || '获取积分排行榜失败');
      }
    } catch (error) {
      console.error('获取积分排行榜失败:', error);
      message.error('获取积分排行榜失败');
    } finally {
      setRankingLoading(false);
    }
  };

  const handleItemSelect = (item) => {
    const pointType = normalizePointType(item.type);
    setSelectedItem({ ...item, type: pointType });
    setBatchDraft({ pointType, category: item.category });
    batchForm.setFieldsValue({
      pointType,
      points: Math.abs(Number(item.points || 0)),
      category: item.category,
      reason: item.name
    });
    if (item.pool_item_id || item.user_template_item_id) {
      pointsTemplatesAPI.recordUsage({
        pool_item_id: item.pool_item_id,
        user_template_item_id: item.user_template_item_id
      }).catch((error) => console.error('记录模板使用次数失败:', error));
    }
  };

  const handleQuickTemplate = (item) => {
    const pointType = normalizePointType(item.item_type);
    setSelectedItem({
      id: item.id,
      name: item.item_name || item.name,
      type: pointType,
      category: item.category,
      points: item.points_value || item.default_points,
      pool_item_id: item.pool_item_id,
      user_template_item_id: item.id
    });
    setBatchDraft({ pointType, category: item.category });
    batchForm.setFieldsValue({
      pointType,
      points: Math.abs(Number(item.points_value || item.default_points || 0)),
      category: item.category,
      reason: item.item_name || item.name
    });
  };

  const handleBatchSubmit = async () => {
    if (selectedRegisterEntries.length === 0) {
      message.warning('请先选择学生');
      return;
    }
    try {
      const values = await batchForm.validateFields();
      setSubmitting(true);
      const selectedTemplateItem = selectedItem &&
        selectedItem.name === values.reason &&
        selectedItem.category === values.category &&
        selectedItem.type === normalizePointType(values.pointType)
        ? selectedItem
        : null;
      const payloads = selectedRegisterEntries.map((seat) => ({
        student_id: seat.student_id,
        student_name: seat.student_name,
        class_id: selectedClassId,
        school_id: selectedClass?.school_id || user?.schoolId || null,
        point_type: normalizePointType(values.pointType),
        points: toSignedPoints(values.pointType, values.points),
        reason: values.reason,
        category: values.category,
        pool_item_id: selectedTemplateItem?.pool_item_id || undefined,
        user_template_item_id: selectedTemplateItem?.user_template_item_id || undefined,
        userTemplateItemId: selectedTemplateItem?.user_template_item_id || undefined
      }));
      const results = await Promise.allSettled(payloads.map((payload) => studentAPI.points.createPoints(payload)));
      const successCount = results.filter((item) => item.status === 'fulfilled').length;
      const failedCount = results.length - successCount;
      if (successCount) {
        message.success(`已完成 ${successCount} 名学生的积分登记`);
        setSelectedStudentIds([]);
        fetchPointsData();
        fetchRankingData();
      }
      if (failedCount) {
        message.warning(`${failedCount} 名学生提交失败，请稍后重试`);
      }
    } catch (error) {
      if (!error?.errorFields) {
        console.error('批量积分提交失败:', error);
        message.error('批量积分提交失败');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const openRecordModal = (record = null) => {
    setEditingRecord(record);
    if (record) {
      const pointType = normalizePointType(record.point_type);
      setRecordDraft({ pointType, category: record.category || null });
      recordForm.setFieldsValue({
        student_id: record.student_id,
        pointType,
        points: Math.abs(Number(record.points || 0)),
        category: record.category || null,
        reason: record.reason || ''
      });
    } else {
      setRecordDraft({ pointType: 'add', category: null });
      recordForm.resetFields();
      recordForm.setFieldsValue({
        pointType: 'add',
        points: 1
      });
    }
    setRecordModalVisible(true);
  };

  const handleRecordSubmit = async () => {
    try {
      const values = await recordForm.validateFields();
      const student = students.find((item) => String(item.id) === String(values.student_id));
      if (!student) {
        message.error('未找到对应学生');
        return;
      }

      const payload = {
        student_id: student.id,
        student_name: student.student_name,
        class_id: student.class_id || selectedClassId || null,
        school_id: student.school_id || student.schoolId || selectedClass?.school_id || selectedClass?.schoolId || user?.schoolId || null,
        point_type: normalizePointType(values.pointType),
        points: toSignedPoints(values.pointType, values.points),
        reason: values.reason,
        category: values.category
      };

      if (editingRecord) {
        await studentAPI.points.updatePoints(editingRecord.id, payload);
        message.success('积分记录已更新');
      } else {
        await studentAPI.points.createPoints(payload);
        message.success('积分记录已新增');
      }

      setRecordModalVisible(false);
      fetchPointsData();
      fetchRankingData();
    } catch (error) {
      if (!error?.errorFields) {
        console.error('提交积分记录失败:', error);
        message.error('提交积分记录失败');
      }
    }
  };

  const handleDeleteRecord = (record) => {
    Modal.confirm({
      title: '确认删除积分记录',
      content: `确定删除 ${record.student_name} 的这条积分记录吗？`,
      onOk: async () => {
        try {
          await studentAPI.points.deletePoints(record.id);
          message.success('积分记录已删除');
          fetchPointsData();
          fetchRankingData();
        } catch (error) {
          console.error('删除积分记录失败:', error);
          message.error('删除积分记录失败');
        }
      }
    });
  };

  const handleExportRecords = async () => {
    try {
      message.loading({ content: '正在导出积分记录...', key: 'export-points', duration: 0 });
      const rows = await fetchAllPages(studentAPI.points.getPoints, {
        schoolId: selectedClass?.school_id || selectedClass?.schoolId || user?.schoolId || undefined,
        classId: selectedClassId || undefined,
        ...recordFilters
      });
      if (rows.length === 0) {
        message.warning({ content: '没有可导出的积分记录', key: 'export-points' });
        return;
      }
      downloadCsv(`积分记录-${moment().format('YYYYMMDD-HHmmss')}.csv`, [
        { title: '学生姓名', dataIndex: 'student_name' },
        { title: '班级', render: (row) => row.class_name || getClassLabel(selectedClass) },
        { title: '积分变化', dataIndex: 'points' },
        { title: '积分类型', render: (row) => Number(row.points || 0) >= 0 ? '加分' : '扣分' },
        { title: '分类', dataIndex: 'category' },
        { title: '原因', dataIndex: 'reason' },
        { title: '教师', dataIndex: 'teacher_name' },
        { title: '操作人', dataIndex: 'operator_name' },
        { title: '操作时间', render: (row) => row.created_at ? moment(row.created_at).format('YYYY-MM-DD HH:mm:ss') : '' }
      ], rows);
      message.success({ content: `已导出 ${rows.length} 条积分记录`, key: 'export-points' });
    } catch (error) {
      console.error('导出积分记录失败:', error);
      message.error({ content: '导出积分记录失败', key: 'export-points' });
    }
  };

  const toggleStudent = (studentId) => {
    setSelectedStudentIds((prev) => (prev.includes(studentId) ? prev.filter((item) => item !== studentId) : [...prev, studentId]));
  };

  const seatAt = (row, col) => seats.find((item) => toNumber(item.row_number) === row && toNumber(item.col_number) === col);

  const recordColumns = [
    { title: '学生', dataIndex: 'student_name', key: 'student_name', width: 120 },
    { title: '班级', dataIndex: 'class_name', key: 'class_name', width: 120, render: (value) => value || getClassLabel(selectedClass) },
    {
      title: '积分变化',
      dataIndex: 'points',
      key: 'points',
      width: 110,
      render: (value) => (
        <Tag color={Number(value) > 0 ? 'green' : 'red'} icon={Number(value) > 0 ? <PlusCircleOutlined /> : <MinusCircleOutlined />}>
          {Number(value) > 0 ? '+' : ''}
          {value}
        </Tag>
      )
    },
    { title: '分类', dataIndex: 'category', key: 'category', width: 120, render: (value) => <Tag color="blue">{value || '未分类'}</Tag> },
    { title: '原因', dataIndex: 'reason', key: 'reason', ellipsis: true },
    { title: '教师', dataIndex: 'teacher_name', key: 'teacher_name', width: 120, render: (value) => value || '-' },
    { title: '时间', dataIndex: 'created_at', key: 'created_at', width: 160, render: (value) => (value ? moment(value).format('YYYY-MM-DD HH:mm') : '-') },
    {
      title: '操作',
      key: 'action',
      width: 140,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openRecordModal(record)}>
            编辑
          </Button>
          <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteRecord(record)}>
            删除
          </Button>
        </Space>
      )
    }
  ];

  const rankingColumns = [
    { title: '排名', dataIndex: 'rank', key: 'rank', width: 80, render: (value) => <strong>{value}</strong> },
    { title: '学生', dataIndex: 'student_name', key: 'student_name', width: 120 },
    {
      title: '总积分',
      dataIndex: 'total_points',
      key: 'total_points',
      width: 110,
      render: (value) => <Tag color={Number(value) > 0 ? 'green' : Number(value) < 0 ? 'red' : 'default'}>{Number(value) > 0 ? '+' : ''}{value}</Tag>
    },
    { title: '加分次数', dataIndex: 'earned_count', key: 'earned_count', width: 100 },
    { title: '扣分次数', dataIndex: 'deducted_count', key: 'deducted_count', width: 100 },
    { title: '记录数', dataIndex: 'total_records', key: 'total_records', width: 100 },
    { title: '最近操作', dataIndex: 'last_activity', key: 'last_activity', width: 160, render: (value) => (value ? moment(value).format('YYYY-MM-DD HH:mm') : '-') }
  ];

  const renderStudentPicker = () => {
    if (!selectedClassId) {
      return <Empty description="请先选择班级" />;
    }
    if (seatsLoading) {
      return (
        <div style={{ minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin />
        </div>
      );
    }
    if (!showSeatGrid) {
      if (!rosterForRegister.length) {
        return <Empty description="当前班级暂无学生，或尚未加入本班学籍" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
      }
      return (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="本班尚未在座位图中排座，以下为班级学生名单，可直接点选进行积分登记。"
          />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {rosterForRegister.map((row) => {
              const selected = selectedStudentIds.includes(row.student_id);
              return (
                <div
                  key={row.student_id}
                  onClick={() => toggleStudent(row.student_id)}
                  style={{
                    width: 88,
                    minHeight: 54,
                    padding: '8px 6px',
                    borderRadius: 10,
                    border: selected ? '2px solid #1677ff' : '1px solid #d9e6ff',
                    background: selected ? 'linear-gradient(180deg, #e6f4ff 0%, #ffffff 100%)' : '#ffffff',
                    boxShadow: selected ? '0 6px 16px rgba(22,119,255,0.14)' : '0 4px 10px rgba(15,23,42,0.05)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: '#1f1f1f',
                        lineHeight: 1.2,
                        textAlign: 'center',
                        wordBreak: 'break-all'
                      }}
                    >
                      {row.student_name}
                    </span>
                    {selected ? <CheckCircleOutlined style={{ color: '#1677ff', fontSize: 12, flex: 'none' }} /> : null}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      );
    }

    return (
      <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
        <div style={{ minWidth: Math.max((seatLayout.cols || 0) * 78, 560) }}>
          {Array.from({ length: seatLayout.rows || 0 }, (_, rowIndex) => {
              const row = rowIndex + 1;
              return (
                <div key={row} style={{ display: 'flex', alignItems: 'stretch', marginBottom: 8 }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {Array.from({ length: seatLayout.cols || 0 }, (_, colIndex) => {
                      const col = colIndex + 1;
                      const seat = seatAt(row, col);
                      const hasStudent = Boolean(seat?.student_id && seat?.student_name);
                      const selected = hasStudent && selectedStudentIds.includes(seat.student_id);
                      return (
                        <React.Fragment key={`${row}-${col}`}>
                          <div
                            onClick={() => hasStudent && toggleStudent(seat.student_id)}
                            style={{
                              width: 70,
                              minHeight: 54,
                              padding: '8px 6px',
                              borderRadius: 10,
                              border: selected ? '2px solid #1677ff' : '1px solid #d9e6ff',
                              background: selected ? 'linear-gradient(180deg, #e6f4ff 0%, #ffffff 100%)' : '#ffffff',
                              boxShadow: selected ? '0 6px 16px rgba(22,119,255,0.14)' : '0 4px 10px rgba(15,23,42,0.05)',
                              cursor: hasStudent ? 'pointer' : 'default',
                              opacity: seat ? 1 : 0.3,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span style={{ fontSize: 13, fontWeight: 600, color: '#1f1f1f', lineHeight: 1.2, textAlign: 'center', wordBreak: 'break-all' }}>
                                {seat?.student_name || ''}
                              </span>
                              {selected ? <CheckCircleOutlined style={{ color: '#1677ff', fontSize: 12, flex: 'none' }} /> : null}
                            </div>
                          </div>
                          {seatLayout.aislePositions.includes(col) && col < (seatLayout.cols || 0) ? (
                            <div style={{ width: 10, minHeight: 54, borderRadius: 999, background: 'linear-gradient(180deg, #f0f5ff 0%, #fafafa 100%)', border: '1px dashed #d6e4ff' }} />
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    );
  };

  const renderRegisterTab = () => (
    <Row gutter={[16, 16]}>
      <Col xs={24} xl={16}>
        <Card
          title={<Space><AppstoreOutlined /><span>座位图点名区</span>{selectedClass ? <Tag color="blue">{getClassLabel(selectedClass)}</Tag> : null}</Space>}
          extra={
            <Space>
              <Button
                onClick={() => setSelectedStudentIds(registerSelectableList.map((item) => item.student_id))}
                disabled={!registerSelectableList.length}
              >
                全选学生
              </Button>
              <Button onClick={() => setSelectedStudentIds([])} disabled={!selectedStudentIds.length}>
                清空选择
              </Button>
            </Space>
          }
        >
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={
              showSeatGrid
                ? isTeacher
                  ? '先在座位图中选择学生，再在右侧完成批量加减分。'
                  : '管理员也可按班级使用座位图快速积分登记。'
                : '未排座时系统将列出本班学生，选择方式与座位图一致。'
            }
          />
          {renderStudentPicker()}
        </Card>
      </Col>
      <Col xs={24} xl={8}>
        <div style={{ position: 'sticky', top: 24 }}>
          <Card title={<Space><PlusCircleOutlined /><span>批量积分操作</span></Space>} style={{ marginBottom: 16 }}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <div style={{ padding: 12, borderRadius: 12, background: '#f5faff', border: '1px solid #d6e4ff' }}>
                <div style={{ fontSize: 13, color: '#8c8c8c', marginBottom: 8 }}>已选择学生</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#1677ff', lineHeight: 1 }}>{selectedStudentIds.length}</div>
                <div style={{ marginTop: 8 }}>
                  <Space wrap size={[6, 6]}>
                    {selectedRegisterEntries.slice(0, 8).map((seat) => (
                      <Tag key={seat.student_id} color="blue">
                        {seat.student_name}
                      </Tag>
                    ))}
                    {selectedRegisterEntries.length > 8 ? <Tag>+{selectedRegisterEntries.length - 8}</Tag> : null}
                  </Space>
                </div>
              </div>

              <Form
                form={batchForm}
                layout="vertical"
                initialValues={{ pointType: 'add', points: 1 }}
                onValuesChange={(_, values) => setBatchDraft({ pointType: normalizePointType(values.pointType || batchDraft.pointType), category: values.category || null })}
              >
                <Form.Item label="积分方向" name="pointType" rules={[{ required: true, message: '请选择积分方向' }]}>
                  <Radio.Group optionType="button" buttonStyle="solid" style={{ width: '100%' }}>
                    <Radio.Button value="add" style={{ width: '50%', textAlign: 'center' }}>加分</Radio.Button>
                    <Radio.Button value="subtract" style={{ width: '50%', textAlign: 'center' }}>减分</Radio.Button>
                  </Radio.Group>
                </Form.Item>
                <Form.Item label="分值" name="points" rules={[{ required: true, message: '请输入分值' }]}>
                  <InputNumber min={0.1} max={50} step={0.1} precision={1} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item label="快捷项目">
                  <Space wrap>
                    <Button icon={<SearchOutlined />} onClick={() => setSelectorVisible(true)}>从项目库选择</Button>
                    {quickTemplateItems.map((item) => <Tag key={item.id} color={normalizePointType(item.item_type) === 'add' ? 'green' : 'red'} style={{ cursor: 'pointer', padding: '4px 8px' }} onClick={() => handleQuickTemplate(item)}>{item.item_name || item.name}</Tag>)}
                  </Space>
                  {selectedItem ? <div style={{ marginTop: 10 }}><Tag color={selectedItem.type === 'add' ? 'green' : 'red'}>{selectedItem.type === 'add' ? '加分' : '减分'}</Tag><Tag>{selectedItem.category}</Tag><Tag>{Math.abs(Number(selectedItem.points || 0))}分</Tag></div> : null}
                </Form.Item>
                <Form.Item label="分类" name="category" rules={[{ required: true, message: '请选择分类' }]}>
                  <CategoryClickPicker onAfterPick={() => batchForm.setFieldsValue({ reason: '' })} />
                </Form.Item>
                <Form.Item
                  noStyle
                  shouldUpdate={(prev, cur) => prev.category !== cur.category || prev.pointType !== cur.pointType}
                >
                  {() => {
                    const cat = batchForm.getFieldValue('category');
                    const pt = batchForm.getFieldValue('pointType');
                    const opts = getReasonsForCategory(cat, toReasonType(pt));
                    return (
                      <Form.Item label="原因" name="reason" rules={[{ required: true, message: '请选择或填写原因' }]}>
                        <ReasonClickPicker reasonOptions={opts} />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Form>

              <Button type="primary" size="large" block loading={submitting} onClick={handleBatchSubmit} disabled={!selectedStudentIds.length}>
                提交给 {selectedStudentIds.length} 名学生
              </Button>
            </Space>
          </Card>

          <Card title="班级状态" size="small">
            <Space direction="vertical" style={{ width: '100%' }} size={10}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{showSeatGrid ? '已排座学生' : '本班学生（可点名）'}</span>
                <strong>{registerSelectableList.length}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>当前班级</span>
                <strong>{selectedClass ? getClassLabel(selectedClass) : '未选择'}</strong>
              </div>
            </Space>
          </Card>
        </div>
      </Col>
    </Row>
  );

  const renderRecordsTab = () => (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={6}>
            <Select allowClear showSearch placeholder="筛选学生" value={recordFilters.studentId} onChange={(value) => { setRecordsPagination((prev) => ({ ...prev, current: 1 })); setRecordFilters((prev) => ({ ...prev, studentId: value || null })); }} style={{ width: '100%' }} optionFilterProp="children" filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}>
              {classStudents.map((student) => <Option key={student.id} value={student.id}>{student.student_name}</Option>)}
            </Select>
          </Col>
          <Col xs={12} md={4}>
            <Select allowClear placeholder="积分类型" value={recordFilters.pointType} onChange={(value) => { setRecordsPagination((prev) => ({ ...prev, current: 1 })); setRecordFilters((prev) => ({ ...prev, pointType: value || null })); }} style={{ width: '100%' }}>
              <Option value="add">加分</Option>
              <Option value="subtract">减分</Option>
            </Select>
          </Col>
          <Col xs={12} md={4}>
            <Select allowClear placeholder="积分分类" value={recordFilters.category} onChange={(value) => { setRecordsPagination((prev) => ({ ...prev, current: 1 })); setRecordFilters((prev) => ({ ...prev, category: value || null })); }} style={{ width: '100%' }}>
              {CATEGORY_LIST.map((item) => <Option key={item} value={item}>{item}</Option>)}
            </Select>
          </Col>
          <Col xs={24} md={7}>
            <Space.Compact style={{ width: '100%' }}>
              <Input type="date" value={recordFilters.startDate || ''} onChange={(event) => { setRecordsPagination((prev) => ({ ...prev, current: 1 })); setRecordFilters((prev) => ({ ...prev, startDate: event.target.value || null })); }} />
              <Input type="date" value={recordFilters.endDate || ''} onChange={(event) => { setRecordsPagination((prev) => ({ ...prev, current: 1 })); setRecordFilters((prev) => ({ ...prev, endDate: event.target.value || null })); }} />
            </Space.Compact>
          </Col>
          <Col xs={24} md={3}>
            <Space style={{ width: '100%' }}>
              <Button style={{ flex: 1 }} onClick={() => { setRecordsPagination((prev) => ({ ...prev, current: 1 })); setRecordFilters({ studentId: null, pointType: null, category: null, startDate: null, endDate: null }); }}>重置</Button>
              <Button type="primary" style={{ flex: 1 }} onClick={() => openRecordModal()}>新增</Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Row gutter={16}>
        <Col xs={24} md={8}><Card size="small"><Statistic title="当前页加分" value={statistics.totalAdded} prefix={<PlusCircleOutlined style={{ color: '#52c41a' }} />} /></Card></Col>
        <Col xs={24} md={8}><Card size="small"><Statistic title="当前页扣分" value={statistics.totalDeducted} prefix={<MinusCircleOutlined style={{ color: '#ff4d4f' }} />} /></Card></Col>
        <Col xs={24} md={8}><Card size="small"><Statistic title="当前页净积分" value={statistics.netPoints} prefix={<TrophyOutlined style={{ color: '#1677ff' }} />} /></Card></Col>
      </Row>

      <Card
        title="积分记录"
        extra={<Button icon={<DownloadOutlined />} onClick={handleExportRecords}>导出记录</Button>}
      >
        <Table
          rowKey="id"
          loading={recordsLoading}
          columns={recordColumns}
          dataSource={pointsData}
          scroll={{ x: 1000 }}
          pagination={{ ...recordsPagination, showSizeChanger: true, showQuickJumper: true, showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条` }}
          onChange={(pagination) => setRecordsPagination((prev) => ({ ...prev, current: pagination.current, pageSize: pagination.pageSize }))}
        />
      </Card>
    </Space>
  );

  const renderRankingTab = () => {
    const totalStudents = rankingData.length;
    const topScore = totalStudents ? rankingData[0].total_points : 0;
    const avgScore = totalStudents ? (rankingData.reduce((sum, item) => sum + Number(item.total_points || 0), 0) / totalStudents).toFixed(1) : 0;
    return (
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Card>
          <Row gutter={[12, 12]} align="middle">
            <Col flex="auto"><Space><Tag color="gold">{selectedClass ? getClassLabel(selectedClass) : '未选择班级'}</Tag><span style={{ color: '#8c8c8c' }}>排行榜按当前选中班级展示</span></Space></Col>
            <Col><Space><Select style={{ width: 120 }} value={rankingLimit} onChange={setRankingLimit}><Option value={10}>前10名</Option><Option value={20}>前20名</Option><Option value={50}>前50名</Option><Option value={100}>前100名</Option></Select><Button onClick={fetchRankingData}>刷新</Button></Space></Col>
          </Row>
        </Card>
        <Row gutter={16}>
          <Col xs={24} md={8}><Card size="small"><Statistic title="上榜人数" value={totalStudents} prefix={<TeamOutlined />} /></Card></Col>
          <Col xs={24} md={8}><Card size="small"><Statistic title="最高积分" value={topScore} prefix={<TrophyOutlined style={{ color: '#faad14' }} />} /></Card></Col>
          <Col xs={24} md={8}><Card size="small"><Statistic title="平均积分" value={avgScore} prefix={<TrophyOutlined style={{ color: '#1677ff' }} />} /></Card></Col>
        </Row>
        <Card title="积分排行榜">
          <Table rowKey="student_id" loading={rankingLoading} columns={rankingColumns} dataSource={rankingData} pagination={false} locale={{ emptyText: selectedClassId ? '当前班级暂无排行数据' : '请先选择班级' }} scroll={{ x: 860 }} />
        </Card>
      </Space>
    );
  };

  const renderSettingsTab = () => (
    <Card title="积分设置">
      <Tabs defaultActiveKey="system">
        <TabPane tab="系统模板" key="system">
          <SystemTemplates onSelectTemplate={(template) => { setCurrentTemplate(template); loadTemplateItems(template.id); message.success('已切换到所选模板'); }} />
        </TabPane>
        <TabPane tab="我的模板" key="custom">
          <CustomTemplates templates={templates} currentTemplate={currentTemplate} onLoadTemplate={(template) => { setCurrentTemplate(template); loadTemplateItems(template.id); }} onRefresh={fetchTemplates} />
        </TabPane>
        <TabPane tab="模板项目" key="items">
          <TemplateItems items={templateItems} currentTemplate={currentTemplate} onRefresh={() => currentTemplate && loadTemplateItems(currentTemplate.id)} />
        </TabPane>
      </Tabs>
    </Card>
  );

  const tabs = isTeacher
    ? [
        { key: 'register', title: '积分登记', render: renderRegisterTab },
        { key: 'records', title: '积分记录', render: renderRecordsTab },
        { key: 'ranking', title: '积分排行榜', render: renderRankingTab },
        { key: 'settings', title: '积分设置', render: renderSettingsTab }
      ]
    : [
        { key: 'records', title: '积分记录', render: renderRecordsTab },
        { key: 'ranking', title: '积分排行榜', render: renderRankingTab },
        { key: 'settings', title: '积分设置', render: renderSettingsTab },
        { key: 'register', title: '积分登记', render: renderRegisterTab }
      ];

  return (
    <Spin spinning={pageLoading}>
      <div style={{ padding: 24 }}>
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]} align="middle" justify="space-between">
            <Col>
              <Space direction="vertical" size={4}>
                <Space><TrophyOutlined style={{ color: '#1677ff', fontSize: 22 }} /><span style={{ fontSize: 22, fontWeight: 700 }}>{isTeacher ? '课堂积分工作台' : '积分管理中心'}</span></Space>
                <span style={{ color: '#8c8c8c' }}>{isTeacher ? '教师进入即看座位图，可批量选择学生并完成加减分。' : '管理员默认查看记录与统计，也可按班级进入座位图快速登记。'}</span>
              </Space>
            </Col>
            <Col>
              <Space wrap>
                <span style={{ color: '#595959' }}>当前班级</span>
                <Select style={{ width: 240 }} value={selectedClassId} onChange={setSelectedClassId} allowClear={!isTeacher} placeholder="请选择班级">
                  {classes.map((item) => <Option key={item.id} value={item.id}>{getClassLabel(item)}</Option>)}
                </Select>
              </Space>
            </Col>
          </Row>
        </Card>

        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          {tabs.map((tab) => <TabPane tab={tab.title} key={tab.key}>{tab.render()}</TabPane>)}
        </Tabs>

        <Modal
          title={editingRecord ? '编辑积分记录' : '新增积分记录'}
          open={recordModalVisible}
          onCancel={() => setRecordModalVisible(false)}
          onOk={handleRecordSubmit}
          destroyOnClose
        >
          <Form
            form={recordForm}
            layout="vertical"
            initialValues={{ pointType: 'add', points: 1 }}
            onValuesChange={(_, values) =>
              setRecordDraft({
                pointType: normalizePointType(values.pointType || recordDraft.pointType),
                category: values.category || null
              })
            }
          >
            <Form.Item name="student_id" label="学生" rules={[{ required: true, message: '请选择学生' }]}>
              <Select
                showSearch
                placeholder="请选择学生"
                optionFilterProp="children"
                filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
              >
                {students.map((student) => (
                  <Option key={student.id} value={student.id}>
                    {student.student_name} - {student.class_name || getClassLabel(classes.find((item) => String(item.id) === String(student.class_id)))}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="pointType" label="积分方向" rules={[{ required: true, message: '请选择积分方向' }]}>
                  <Select>
                    <Option value="add">加分</Option>
                    <Option value="subtract">减分</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="points" label="分值" rules={[{ required: true, message: '请输入分值' }]}>
                  <InputNumber min={0.1} max={50} step={0.1} precision={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="category" label="分类" rules={[{ required: true, message: '请选择分类' }]}>
              <CategoryClickPicker onAfterPick={() => recordForm.setFieldsValue({ reason: '' })} />
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prev, cur) => prev.category !== cur.category || prev.pointType !== cur.pointType}
            >
              {() => {
                const cat = recordForm.getFieldValue('category');
                const pt = recordForm.getFieldValue('pointType');
                const opts = getReasonsForCategory(cat, toReasonType(pt));
                return (
                  <Form.Item name="reason" label="原因" rules={[{ required: true, message: '请选择或填写原因' }]}>
                    <ReasonClickPicker reasonOptions={opts} />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Form>
        </Modal>

        <PointsItemSelector visible={selectorVisible} onClose={() => setSelectorVisible(false)} onSelect={handleItemSelect} itemType={batchDraft.pointType === 'add' ? 'add' : 'deduct'} schoolStage="初中" />
      </div>
    </Spin>
  );
};

export default StudentPointsWorkbench;
